import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { PricingParameters } from '../types';

interface PricingStore {
  parameters: PricingParameters;
  manualTargetFCR: number | null;
  updateParameter: <K extends keyof PricingParameters>(key: K, value: PricingParameters[K]) => void;
  setManualTargetFCR: (value: number | null) => void;
  loadDefaults: () => void;
  saveDefaults: () => void;
  reset: () => void;
}

// Factory default values
const factoryDefaults: PricingParameters = {
  meatPricePerKg: 8.0,
  feedBonusRate: 9.0,  // ₺/kg
  feedPenaltyRate: 11.0,  // ₺/kg
  chickBonusRate: 18.4,  // ₺/adet
  chickPenaltyRate: 18.4,  // ₺/adet
  chickMortalityLimit: 5.0,  // %5 fire limiti
  epefLimit1: 301,
  epefPrice1: 0.10,  // 10 kuruş
  epefLimit2: 351,
  epefPrice2: 0.15,  // 15 kuruş
  epefLimit3: 371,
  epefPrice3: 0.20,  // 20 kuruş
  coalSupportPerTon: 0.75,  // 75 kuruş
};

const loadDefaultsFromStorage = (): PricingParameters => {
  try {
    const stored = localStorage.getItem('broiler_defaults_pricing');
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (error) {
    console.error('Failed to load pricing defaults:', error);
  }
  return { ...factoryDefaults };
};

const saveDefaultsToStorage = (parameters: PricingParameters): void => {
  try {
    localStorage.setItem('broiler_defaults_pricing', JSON.stringify(parameters));
  } catch (error) {
    console.error('Failed to save pricing defaults:', error);
  }
};

export const usePricingStore = create<PricingStore>()(
  persist(
    (set, get) => ({
      parameters: loadDefaultsFromStorage(),
      manualTargetFCR: null,

      updateParameter: (key, value) => {
        set((state) => ({
          parameters: {
            ...state.parameters,
            [key]: value,
          },
        }));
      },

      setManualTargetFCR: (value) => {
        set({ manualTargetFCR: value });
      },

      loadDefaults: () => {
        const defaults = loadDefaultsFromStorage();
        set({ parameters: defaults });
      },

      saveDefaults: () => {
        const { parameters } = get();
        saveDefaultsToStorage(parameters);
      },

      reset: () => {
        set({
          parameters: { ...factoryDefaults },
          manualTargetFCR: null,
        });
      },
    }),
    {
      name: 'broiler_pricing_data',
      partialize: (state) => ({
        parameters: state.parameters,
        manualTargetFCR: state.manualTargetFCR,
      }),
    }
  )
);

// Export factory defaults for testing
export { factoryDefaults };
